import { Link } from "react-router-dom";
import { common_utils } from "../../../../../../../common/common_utils";
import {
	getRawProductWithoutPrices,
	isBundleProduct,
	PaymentInterval,
	PaymentIntervalToActiveName,
} from "../../../../../../../common/premium_tiers";
import { RouterInferenceHelpers } from "../../../../../../../src/api/trpc/rootRouter";
import { GenericEntitlement } from "./generic_entitlement";

type BaseEntitlement = RouterInferenceHelpers["premium"]["getUserPremiumDetails"]["output"]["stripe_entitlements"][number];

interface StripeEntitlementProps extends BaseEntitlement {
	isUpgradeable?: boolean;
	onUpgrade?: () => void;
}

export function StripeEntitlement(props: StripeEntitlementProps) {
	const { isUpgradeable, onUpgrade, ...entitlementProps } = props;
	const product = getRawProductWithoutPrices(entitlementProps.product_id);
	const isBundle = isBundleProduct(entitlementProps.product_id);
	const proAddonCount = (entitlementProps as any).pro_addon_count ?? 0;

	return (
		<GenericEntitlement
			title={product.title}
			type={"Stripe"}
			modify_link={
				<div className="flex items-center gap-3">
					{isUpgradeable && onUpgrade && (
						<button
							className="flex items-center gap-1 rounded border border-amber-600/50 px-2 py-1 text-xs font-medium text-amber-500 hover:bg-amber-600/20"
							onClick={onUpgrade}
						>
							<i className="fas fa-crown fa-xs" />
							Upgrade to Pro
						</button>
					)}
					{entitlementProps.payment_interval !== PaymentInterval.OneTime && (
						<Link
							className="secondary-link"
							to={common_utils.getStripeRedirectURL({
								type: "manager",
							})}
						>
							Modify
						</Link>
					)}
				</div>
			}
			payment_interval={PaymentIntervalToActiveName[
				entitlementProps.payment_interval
			].toLowerCase()}
			details={
				<>
					{isBundle ? (
						<>
							<div>
								Premium Activations:{" "}
								<span className="font-bold">{entitlementProps.activations}</span>
							</div>
							<div>
								Pro Activations:{" "}
								<span className="font-bold">{proAddonCount}</span>
							</div>
						</>
					) : (
						<div>
							Activations Granted:{" "}
							<span className="font-bold">{entitlementProps.activations}</span>
						</div>
					)}
					<div>
						Status:{" "}
						<span className="font-bold">{entitlementProps.active_status}</span>
					</div>
					{entitlementProps.is_from_admin && (
						<div>
							From admin: <span className="font-bold">true</span>
						</div>
					)}
				</>
			}
		/>
	);
}

export function StripeEntitlementNoModifyButton(
	props: RouterInferenceHelpers["premium"]["getUserPremiumDetails"]["output"]["stripe_entitlements"][number]
) {
	const product = getRawProductWithoutPrices(props.product_id);
	const isBundle = isBundleProduct(props.product_id);
	const proAddonCount = (props as any).pro_addon_count ?? 0;
	const upgradedToPro = (props as any).upgraded_to_pro ?? false;

	return (
		<GenericEntitlement
			title={product.title}
			type={"Stripe"}
			payment_interval={PaymentIntervalToActiveName[
				props.payment_interval
			].toLowerCase()}
			details={
				<>
					{isBundle ? (
						<>
							<div>
								Premium Activations:{" "}
								<span className="font-bold">{props.activations}</span>
							</div>
							<div>
								Pro Activations:{" "}
								<span className="font-bold">{proAddonCount}</span>
							</div>
						</>
					) : (
						<div>
							Activations Granted:{" "}
							<span className="font-bold">{props.activations}</span>
						</div>
					)}
					<div>
						Status:{" "}
						<span className="font-bold">{props.active_status ?? "active"}</span>
					</div>
					{props.is_from_admin && (
						<div>
							From admin: <span className="font-bold">true</span>
						</div>
					)}
					{upgradedToPro && (
						<div>
							Upgraded to Pro:{" "}
							<span className="font-bold text-amber-400">Yes</span>
						</div>
					)}
				</>
			}
		/>
	);
}
